import { useState, useCallback } from 'react';

type UseModalReturn<T, O = unknown> = [boolean, () => void, () => void, (arg: T, options?: O) => void];

/**
 * This hook drives the opening/closing of modals.
 * It returns an array with 4 items:
 * - [0]: isOpen: boolean that drives whether to show or hide the modal
 * - [1]: open: function to call to show/open the modal
 * - [2]: close: function to call to hide/close the modal
 * - [3]: submit: function to call when the modal is submitted
 * @template T The type of the argument passed to the submit function
 * @template O The type of the options object passed to the submit function
 * @param {(arg: T, options?: O) => void | undefined} [onSubmit] Callback called when submit() is called
 * @param {boolean} [isInitiallyOpen=false] Whether the modal should be open initially
 * @returns {UseModalReturn<T, O>} Array containing [isOpen, open, close, submit]
 */
export default function useModal<T, O = unknown>(
  onSubmit?: (arg: T, options?: O) => void | undefined,
  isInitiallyOpen: boolean = false,
): UseModalReturn<T, O> {
  const [isOpen, setOpen] = useState<boolean>(isInitiallyOpen);

  const open = useCallback(() => {
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const submit = useCallback(
    (arg: T, options?: O) => {
      if (onSubmit) {
        setOpen(false);
        onSubmit(arg, options);
      }
    },
    [onSubmit],
  );

  return [isOpen, open, close, submit];
}
